import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AureloLogo, AureloMark } from "@/components/Logo";
import { DevelopedBy } from "@/components/CodeLabsLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";

const GoogleIcon = (props) => (
  <svg viewBox="0 0 48 48" width="18" height="18" {...props}>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 7 29.6 5 24 5c-7.5 0-13.9 4.3-17.7 9.7z"/>
    <path fill="#4CAF50" d="M24 44c5.5 0 10.4-1.9 14-5l-6.5-5.3c-2 1.4-4.6 2.3-7.5 2.3-5.3 0-9.7-3.4-11.3-8.1l-6.6 5.1C9.9 39.6 16.4 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.5 5.3C41.3 36 44 30.5 44 24c0-1.3-.1-2.7-.4-3.5z"/>
  </svg>
);

const continueWithGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const AuthShell = ({ children }) => (
  <div className="min-h-screen grid lg:grid-cols-2">
    <div className="flex items-center justify-center p-6">
      <div className="w-full max-w-sm">{children}</div>
    </div>
    <div className="hidden lg:flex relative bg-[#0B102B] items-center justify-center p-12 overflow-hidden">
      <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full aurelo-gradient opacity-30 blur-3xl" />
      <div className="relative text-white max-w-md">
        <AureloLogo size={32} light />
        <h2 className="text-3xl font-extrabold mt-8 tracking-tight leading-tight">The AI-native Revenue Operating System.</h2>
        <p className="text-slate-300 mt-3">CRM, sales engagement, marketing, AI copilot and customer success — unified.</p>
        <div className="mt-8 space-y-3">
          {["AI Copilot on every record","Autonomous AI SDR agents","Revenue intelligence & forecasting"].map((x) => (
            <div key={x} className="flex items-center gap-2 text-slate-200"><Sparkles className="h-4 w-4 text-[#A855F7]" />{x}</div>
          ))}
        </div>
        <div className="mt-10 pt-6 border-t border-white/10"><DevelopedBy labelColor="#64748B" height={22} /></div>
      </div>
    </div>
  </div>
);

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await login(email, password); toast.success("Welcome back!"); navigate("/app"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Invalid credentials"); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell>
      <div className="lg:hidden mb-8"><AureloLogo size={30} /></div>
      <h1 className="text-2xl font-bold tracking-tight">Welcome back</h1>
      <p className="text-sm text-slate-500 mt-1">Sign in to your Aurelo workspace.</p>
      <Button variant="outline" className="w-full mt-6" onClick={continueWithGoogle} data-testid="login-google-btn">
        <GoogleIcon className="mr-2" />Continue with Google
      </Button>
      <div className="relative my-5"><div className="border-t border-slate-200" /><span className="absolute inset-0 -top-2.5 flex justify-center"><span className="bg-white px-2 text-xs text-slate-400">or</span></span></div>
      <form onSubmit={submit} className="space-y-4">
        <div><Label htmlFor="email">Email</Label><Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" required /></div>
        <div><Label htmlFor="password">Password</Label><Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" required /></div>
        <Button type="submit" data-testid="login-submit" className="w-full aurelo-gradient text-white" disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In <ArrowRight className="h-4 w-4 ml-1" /></>}</Button>
      </form>
      <p className="text-sm text-slate-500 mt-6 text-center">No account? <Link to="/signup" className="font-semibold text-[#7C3AED] hover:underline">Start free trial</Link></p>
    </AuthShell>
  );
}

export function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", organization: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await signup(form); toast.success("Workspace created!"); navigate("/onboarding"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not sign up"); }
    finally { setLoading(false); }
  };
  return (
    <AuthShell>
      <div className="lg:hidden mb-8"><AureloLogo size={30} /></div>
      <h1 className="text-2xl font-bold tracking-tight">Create your workspace</h1>
      <p className="text-sm text-slate-500 mt-1">Start your AI-powered revenue engine — 7-day free trial, cancel anytime.</p>
      <Button variant="outline" className="w-full mt-6" onClick={continueWithGoogle} data-testid="signup-google-btn">
        <GoogleIcon className="mr-2" />Continue with Google
      </Button>
      <div className="relative my-5"><div className="border-t border-slate-200" /><span className="absolute inset-0 -top-2.5 flex justify-center"><span className="bg-white px-2 text-xs text-slate-400">or</span></span></div>
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Full name</Label><Input data-testid="signup-name" value={form.name} onChange={set("name")} className="mt-1.5" required /></div>
        <div><Label>Organization</Label><Input data-testid="signup-org" value={form.organization} onChange={set("organization")} className="mt-1.5" required /></div>
        <div><Label>Work email</Label><Input data-testid="signup-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5" required /></div>
        <div><Label>Password</Label><Input data-testid="signup-password" type="password" value={form.password} onChange={set("password")} className="mt-1.5" required minLength={6} /></div>
        <Button type="submit" data-testid="signup-submit" className="w-full aurelo-gradient text-white" disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="h-4 w-4 ml-1" /></>}</Button>
      </form>
      <p className="text-sm text-slate-500 mt-6 text-center">Already have an account? <Link to="/login" className="font-semibold text-[#7C3AED] hover:underline">Sign in</Link></p>
    </AuthShell>
  );
}
